/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { useState } from 'react';
import { BasicAlert } from '../../../components/alert/BasicAlert';
import Footer from '../../../components/footer';
import RequestDemoModal from '../../../components/request-demo-modal';
import { getAppFlavourCards } from '../partials/cards';
import { HomepageCardGrid } from '../partials/HomepageCard';
import { TestimonialsContainer } from '../../../components/testimonials/TestimonialsContainer';
import { NetworkSection } from './NetworkSection';
import { useSelector } from 'react-redux';
import { RootState, useAppSelector } from '../../../reducers';
import SGXLogo from '../../../images/sgx-first-logo.png';
import G17EcoLogo from '../../../images/g17Eco.svg';
import { AppFlavour } from '../../../config/app-config';
import './styles.scss';
import { RegionSwitcher } from './RegionSwitcher';
import { CarbonCalculatorsCallToAction } from '../partials/CarbonCalculatorsCallToAction';
import { isStaff } from '../../../selectors/user';
import { PartnerSectionSimple } from './PartnerSectionSimple';

interface ModalState {
  title?: JSX.Element,
  isPartner?: boolean
  isOpen: boolean;
}

export function SingaporeHome() {

  const [requestModalOpen, setRequestDemoModalOpen] = useState<ModalState>({
    isOpen: false,
    title: <></>,
    isPartner: false,
  });
  const [submitMessage, setSubmitMessage] = useState('');
  const isLoggedIn = useSelector((state: RootState) => state.currentUser.loaded);
  const isUserStaff = useAppSelector(isStaff);

  const toggleRequestDemoModal = (props?: Partial<ModalState>) => {
    setRequestDemoModalOpen({
      title: props?.title ?? <></>,
      isPartner: Boolean(props?.isPartner),
      isOpen: !requestModalOpen.isOpen,
    });
  };

  const cards = getAppFlavourCards(AppFlavour.SINGAPORE, isLoggedIn, isUserStaff);
  const customTitle = requestModalOpen.title?.props.children;
  const title = customTitle ? `Get in touch with ${customTitle}` : undefined;

  return (
    <div className='homepage-container d-flex flex-column p-4'>
      <BasicAlert className={'text-center success-alert-container'} type={'success'}>{submitMessage}</BasicAlert>
      <div className='homepage-content flex-fill'>
        <div className='hp-header'>
          <div className='section col-12 d-flex align-items-center'>
            {/* Use of flag in this context might violate country's rules so removing until we know more */}
            {/* <img draggable={false} className='col-3 col-md-1 border' src={SingaporeFlag} alt='SingaporeFlag' /> */}
            <span className='h3 text-uppercase text-ThemeTextLight'>Singapore Genome</span>
          </div>
          <div className='d-flex align-items-center'>
            <div className='flex-fill'>
              <h1>Sustainability Data Utility</h1>
            </div>
            <div className='text-right'>
              <RegionSwitcher />
            </div>
          </div>
          <div className='mt-3'>
            <p className='text-xl text-ThemeTextLight'>SGX ESGenome is now live to support all SGX listed companies in their ESG disclosure journey. We are also developing the National Utility for Sustainability, which is in proof of concept stage. These tools and services are designed to standardise and streamline your sustainability data and reporting end to end, and help you map, monitor, measure, manage and market your sustainability performance and contribution to the 17 Sustainable Development Goals.</p>
          </div>
        </div>

        <CarbonCalculatorsCallToAction size='sm' />

        <div className='mt-6 homepage-inner-container'>
          <h3>Solutions</h3>
          <HomepageCardGrid cards={cards.filter(card => card.solutions)} toggleRequestDemoModal={toggleRequestDemoModal} />

          <h3>Coming soon</h3>
          <HomepageCardGrid cards={cards.filter(card => card.comingSoon)} toggleRequestDemoModal={toggleRequestDemoModal} />
        </div>

        <CarbonCalculatorsCallToAction size='lg' />

        <div className='mt-6 homepage-inner-container'>

          <PartnerSectionSimple mode='asean' />

          {/* <div className='text-center'>
            <Button color='primary' className='join-btn' onClick={() => toggleRequestDemoModal()}>Join G17Eco as a partner</Button>
          </div> */}

          <h3 className='mt-6'>G17Eco member testimonials</h3>
          <div className='mt-4'>
            <TestimonialsContainer tags={['singapore']} />
          </div>

          <div className='mt-6'>
            <NetworkSection />
          </div>

          <div className='mt-5 text-center text-ThemeTextPlaceholder'>
            <div className='row sgx-logo'>
              <div className='offset-4 col-4'><p className='collab-text'>In collaboration with</p></div>
              <div className='offset-4 col-4'><img src={SGXLogo} width={200} alt='SGX First' /></div>
              <div className='col-4 d-flex justify-content-end align-items-center'>
                <div className='mr-2'>Powered by</div>
                <div><img src={G17EcoLogo} width={100} alt='G17Eco' /></div>
              </div>
            </div>
          </div>
        </div>

        <RequestDemoModal
          isOpen={requestModalOpen.isOpen}
          toggle={toggleRequestDemoModal}
          onSubmit={() => {
            setSubmitMessage('Message sent. We’ll be in touch shortly.');
            setTimeout(() => setSubmitMessage(''), 10000);
          }}
          interestedIn={requestModalOpen.isPartner ? 'Partners' : ''}
          title={title}
        />
        <div className='mt-5'>
          <Footer />
        </div>
      </div>
    </div>
  )
}
